import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import StarIcon from '@mui/icons-material/Star';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import PlayCircleOutlineOutlinedIcon from '@mui/icons-material/PlayCircleOutlineOutlined';
import UnfoldMoreOutlinedIcon from '@mui/icons-material/UnfoldMoreOutlined';
import UnfoldLessOutlinedIcon from '@mui/icons-material/UnfoldLessOutlined';
import { api } from '../../app/http-common';
import { useCanEdit } from '../../hooks/useFrontegg';
import useBucketData, { sortClients } from './hooks/useBucketData';
import { activeSuggestions } from './constants';
import PublisherRow from './components/PublisherRow';
import UndoToast from './components/UndoToast';
import BucketsLoading from './components/BucketsLoading';
import { SLOTS, SURFACES, saveReturn, peekReturn, clearReturn, restoreScroll } from '../../app/navContext';
import StaleArchiveModal from './components/StaleArchiveModal';
import AppModal from '../../components/AppModal/AppModal';
import './BucketManagerNew.css';

// Spec 2.4 — Recent activity is the default; A–Z is the only alternative.
const SORT_OPTIONS = [
  { key: 'recent', label: 'Recent activity' },
  { key: 'a-z', label: 'A–Z' },
];

export default function PublisherBucketsNew() {
  const canEdit = useCanEdit();
  const navigate = useNavigate();
  const { clients, rawBuckets, loading, error, reload, favourites, toggleFavourite, favouriteBuckets, toggleFavouriteBucket } = useBucketData();

  // §2.1 — seed from the return context so the screen reopens as it was left,
  // rather than resetting to the top with the publisher collapsed.
  const [restore] = useState(() => peekReturn(SURFACES.PUBLISHER_BUCKETS)?.state || null);
  // Same inner scroll container as In Production — see note there.
  const scrollRef = useRef(null);

  const [search, setSearch] = useState(() => restore?.search || '');
  // Follow-up §11 — remember the accordion the user had open BEFORE they typed
  // a search that auto-expanded matching publishers, so clearing the search
  // restores that state instead of leaving every search-hit permanently open.
  const preSearchExpandedRef = useRef(null);
  // §1.6 — the destination picker reads this same key to mirror the current
  // sort. localStorage rather than sessionStorage: this is a display preference,
  // not a navigation payload.
  const [sort, setSort] = useState(() => {
    try {
      const stored = window.localStorage.getItem('alchemiq_pbnew_sort');
      return restore?.sort || stored || 'recent';
    } catch {
      return restore?.sort || 'recent';
    }
  });
  useEffect(() => {
    try { window.localStorage.setItem('alchemiq_pbnew_sort', sort); } catch { /* quota */ }
  }, [sort]);
  const [expanded, setExpanded] = useState(() => new Set(restore?.expanded || []));
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(null);
  const [emailConfirm, setEmailConfirm] = useState(null);
  const [staleArchive, setStaleArchive] = useState(null);
  const undoIdsRef = React.useRef(null);

  useEffect(() => {
    if (!canEdit) navigate('/', { replace: true });
  }, [canEdit, navigate]);

  // §2.1 — bring the originating bucket tile back to the same viewport anchor
  // once the list has rendered, then drop the context so a later plain visit
  // does not inherit it.
  // Same reasoning as In Production: this is an endpoint of the back journey.
  useEffect(() => { clearReturn(SLOTS.SCREEN); }, []);

  const anchorRestoredRef = useRef(false);
  useEffect(() => {
    if (!restore || loading || anchorRestoredRef.current || !canEdit) return;
    const el = restore.anchorBucketId
      ? document.querySelector(`[data-bucket-id="${restore.anchorBucketId}"]`)
      : null;
    // The tile only exists once its publisher group has rendered expanded. Wait
    // rather than burning the one-shot on a miss — the same trap that made the
    // In Production scroll restore silently do nothing.
    if (restore.anchorBucketId && !el) return;
    anchorRestoredRef.current = true;
    if (el) el.scrollIntoView({ block: 'center' });
    else if (restore.scrollTop != null) restoreScroll(() => scrollRef.current, restore.scrollTop);
  }, [restore, loading, canEdit, clients]);

  // Search matches publisher OR bucket name (spec 2.4).
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients;
    return clients
      .map((c) => {
        if (c.clientName.toLowerCase().includes(q)) return c;
        return { ...c, buckets: c.buckets.filter((b) => (b.name || '').toLowerCase().includes(q)) };
      })
      .filter((c) => c.buckets.length > 0);
  }, [clients, search]);

  const ordered = useMemo(() => sortClients(filtered, sort), [filtered, sort]);

  // Follow-up §11 — when the main search matches bucket names, the containing
  // publishers must open automatically so the matching bucket is visible in
  // one action. Clearing the search restores the accordion state that existed
  // before the search began.
  useEffect(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      if (preSearchExpandedRef.current) {
        setExpanded(new Set(preSearchExpandedRef.current));
        preSearchExpandedRef.current = null;
      }
      return;
    }
    if (preSearchExpandedRef.current == null) {
      preSearchExpandedRef.current = [...expanded];
    }
    // Auto-expand ONLY publishers whose bucket-name hit drives the match. A
    // pure publisher-name hit (buckets not filtered) is left to the user.
    const bucketHits = new Set();
    for (const c of clients) {
      if (c.clientName.toLowerCase().includes(q)) continue;
      if (c.buckets.some((b) => (b.name || '').toLowerCase().includes(q))) {
        bucketHits.add(c.clientId);
      }
    }
    if (bucketHits.size === 0) return;
    setExpanded((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const id of bucketHits) { if (!next.has(id)) { next.add(id); changed = true; } }
      return changed ? next : prev;
    });
    // Intentionally omit `expanded` from deps — the effect is search-driven,
    // and re-running on every toggle would fight the user's manual clicks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, clients]);
  // Favouriting MOVES a publisher into Favourites rather than copying it there.
  //
  // Note this diverges from Corrections §2.4, which says a favourite "appears as
  // a shortcut at the top and remains in the complete publisher list". Liad
  // asked for the move behaviour directly — seeing the same publisher twice read
  // as a duplicate-rendering bug rather than as a shortcut.
  const favClients = useMemo(
    () => ordered.filter((c) => favourites.includes(c.clientId)),
    [ordered, favourites],
  );
  // §2.2 — resolve favourited bucket ids into full entries with their client
  // name, so a shortcut can show both. Missing ids (bucket deleted, or its
  // client removed) are silently dropped rather than rendering a dangling row.
  const favouriteBucketEntries = useMemo(() => {
    if (!favouriteBuckets.length || !rawBuckets.length) return [];
    const bucketIndex = new Map(rawBuckets.map((b) => [String(b._id), b]));
    const clientIndex = new Map(clients.map((c) => [String(c.clientId), c.clientName]));
    return favouriteBuckets
      .map((id) => {
        const bucket = bucketIndex.get(String(id));
        if (!bucket) return null;
        // `bucket.clientId` is a populated `{_id, name}` object from the API,
        // not a scalar — pluck the id (fall back to 'unassigned' to match the
        // grouping in useBucketData).
        const clientIdRaw = bucket.clientId?._id || bucket.clientId || 'unassigned';
        return {
          bucket,
          clientName: clientIndex.get(String(clientIdRaw)) || bucket.clientId?.name || '',
        };
      })
      .filter(Boolean);
  }, [favouriteBuckets, rawBuckets, clients]);

  const restClients = useMemo(
    () => ordered.filter((c) => !favourites.includes(c.clientId)),
    [ordered, favourites],
  );

  const toggleRow = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  const expandAll = () => setExpanded(new Set(ordered.map((c) => c.clientId)));
  const collapseAll = () => setExpanded(new Set());

  // §2.1 — record enough to reopen this screen exactly as it looks now.
  const openBucket = (bucket) => {
    saveReturn({
      surface: SURFACES.PUBLISHER_BUCKETS,
      url: '/publisher-buckets-new',
      label: 'All buckets',
      state: {
        search,
        sort,
        expanded: [...expanded],
        anchorBucketId: bucket._id,
        scrollTop: scrollRef.current ? scrollRef.current.scrollTop : 0,
      },
    });
    navigate(`/publisher-buckets-new/bucket/${bucket._id}`);
  };

  // ── Spec 3.1-3.4: publisher-wide link actions ──
  // Included buckets: active, non-archived, NON-EMPTY. Empty buckets are skipped.
  const includedBuckets = (client) =>
    client.buckets.filter((b) => b.isActive !== false && activeSuggestions(b) > 0);

  const handleCopyAllLinks = useCallback(async (client) => {
    const buckets = includedBuckets(client);
    if (buckets.length === 0) {
      setToast({ message: `${client.clientName} has no non-empty buckets to link.`, type: 'error' });
      return;
    }
    setBusy('copy');
    try {
      const res = await api.createPublisherBucketLinks(client.clientId, { silent: true });
      const lines = (res?.links || []).map((l) => `${l.bucketName}: ${l.url}`);
      if (navigator.clipboard) await navigator.clipboard.writeText(lines.join('\n'));
      setToast({ message: `Copied ${lines.length} bucket link${lines.length === 1 ? '' : 's'}.`, type: 'success' });
      reload();
    } catch (err) {
      setToast({ message: err?.response?.data?.error || 'Could not create the bucket links.', type: 'error' });
    } finally {
      setBusy(null);
    }
  }, [reload]);

  const handleEmailAllLinks = useCallback((client) => {
    const buckets = includedBuckets(client);
    if (buckets.length === 0) {
      setToast({ message: `${client.clientName} has no non-empty buckets to send.`, type: 'error' });
      return;
    }
    setEmailConfirm({ client, count: buckets.length });
  }, []);

  const confirmEmailAllLinks = useCallback(async () => {
    const client = emailConfirm?.client;
    setEmailConfirm(null);
    if (!client) return;
    setBusy('email');
    try {
      const res = await api.emailPublisherBucketLinks(client.clientId, { silent: true });
      setToast({ message: `Emailed ${res?.count ?? 0} bucket links to ${res?.recipient || client.clientName}.`, type: 'success' });
      reload();
    } catch (err) {
      // Spec 3.4 — a failure cancels the whole email and names the failed bucket.
      const data = err?.response?.data || {};
      const which = data.failedBucketName ? ` Failed on "${data.failedBucketName}".` : '';
      setToast({ message: `${data.error || 'Email was not sent.'}${which}`, type: 'error' });
    } finally {
      setBusy(null);
    }
  }, [emailConfirm, reload]);

  const renderRows = (list) =>
    list.map((c) => (
      <PublisherRow
        key={c.clientId}
        client={c}
        expanded={expanded.has(c.clientId)}
        onToggle={toggleRow}
        isFavourite={favourites.includes(c.clientId)}
        onToggleFavourite={toggleFavourite}
        favouriteBuckets={favouriteBuckets}
        onToggleFavouriteBucket={toggleFavouriteBucket}
        onOpenBucket={openBucket}
        onCopyAllLinks={handleCopyAllLinks}
        onEmailAllLinks={handleEmailAllLinks}
        onArchiveStale={(cl, rule) => setStaleArchive({
          rule, clientId: cl.clientId, scopeLabel: `${cl.clientName} — all buckets`,
        })}
        busyAction={busy}
      />
    ));

  if (!canEdit) return null;

  return (
    <div className="pbnew">
      {/* Spec 2.2: the Language/Status/Sections filter sidebar is removed. */}
      <main className="pbnew-main pbnew-main-full" ref={scrollRef}>
        {/* Title, actions, search and sort stay pinned while the publisher
            accordion scrolls beneath — same treatment as In Production. With
            every publisher expanded this list runs long, and losing the search
            box meant scrolling back to the top to narrow it. */}
        <div className="pbnew-sticky-head">
        <div className="pbnew-page-head">
          {/* Spec 2.2: no "Updated just now" subtitle, no transfer-workspace button. */}
          <h1 className="pbnew-h1">Publisher Buckets</h1>
          {/* In Production is a separate cross-bucket workspace (spec 1.4). */}
          <button className="pbnew-btn" onClick={() => navigate('/publisher-buckets-new/in-production')}>
            <PlayCircleOutlineOutlinedIcon style={{ fontSize: 16 }} /> In Production
          </button>
        </div>

        <div className="pbnew-toolbar">
          <div className="pbnew-search">
            <SearchOutlinedIcon style={{ fontSize: 18 }} />
            <input
              placeholder="Search publisher or bucket…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="pbnew-toolbar-right">
            <select className="pbnew-select" style={{ width: 'auto' }} value={sort} onChange={(e) => setSort(e.target.value)}>
              {SORT_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>Sort by {o.label}</option>
              ))}
            </select>
            <button className="pbnew-btn" onClick={expanded.size ? collapseAll : expandAll}>
              {expanded.size
                ? <><UnfoldLessOutlinedIcon style={{ fontSize: 16 }} /> Collapse all</>
                : <><UnfoldMoreOutlinedIcon style={{ fontSize: 16 }} /> Expand all</>}
            </button>
          </div>
        </div>
        </div>

        {error && <div className="pbnew-empty">{error}</div>}
        {!error && loading && <BucketsLoading />}
        {!error && !loading && ordered.length === 0 && (
          <div className="pbnew-empty">No publishers match this search.</div>
        )}

        {(favClients.length > 0 || favouriteBucketEntries.length > 0) && (
          <>
            <div className="pbnew-group-head">
              <StarIcon className="star" style={{ fontSize: 14 }} /> Favourites
            </div>
            {/* Follow-up §10 — favourited PUBLISHERS render before favourited
                bucket shortcuts. The prior order surfaced shortcut chips above
                the full publisher rows, which read as noise before the main
                accordion. */}
            {renderRows(favClients)}
            {/* §2.2 — bucket shortcuts show the FULL publisher + bucket names,
                open directly in Bucket Stories, and do not re-order the
                publisher accordion (the bucket stays put in its own group too). */}
            {favouriteBucketEntries.length > 0 && (
              <div className="pbnew-fav-shortcuts">
                {favouriteBucketEntries.map(({ bucket, clientName }) => (
                  <button
                    key={bucket._id}
                    type="button"
                    className="pbnew-fav-shortcut"
                    onClick={() => openBucket(bucket)}
                    title={`${clientName} › ${bucket.name}`}
                  >
                    <StarIcon className="star" style={{ fontSize: 13 }} />
                    <span className="publisher">{clientName}</span>
                    <span className="sep">›</span>
                    <span className="bucket">{bucket.name}</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {/* Header hidden when every publisher is favourited, so the section
            never renders as a bare heading over nothing. */}
        {restClients.length > 0 && (
          <>
            <div className="pbnew-group-head">
              <InventoryOutlinedIcon style={{ fontSize: 14 }} /> All publishers
            </div>
            {renderRows(restClients)}
          </>
        )}
      </main>

      <AppModal
        isOpen={!!emailConfirm}
        title="Email all bucket links"
        onCancel={() => setEmailConfirm(null)}
        onConfirm={confirmEmailAllLinks}
        confirmLabel="Send email"
        maxWidth={460}
      >
        <p style={{ margin: 0, fontSize: 14, color: '#3C4A5E', lineHeight: 1.5 }}>
          A fresh public list link will be created for each of{' '}
          <strong>{emailConfirm?.count}</strong> non-empty bucket
          {emailConfirm?.count === 1 ? '' : 's'} belonging to{' '}
          <strong>{emailConfirm?.client?.clientName}</strong>, and emailed to their configured
          address. If any link fails, nothing is sent.
        </p>
      </AppModal>

      <StaleArchiveModal
        request={staleArchive}
        onClose={() => setStaleArchive(null)}
        onDone={(res) => {
          setStaleArchive(null);
          undoIdsRef.current = res.archivedIds || [];
          setToast({
            message: `${res.archivedCount} suggestion${res.archivedCount === 1 ? '' : 's'} archived.`,
            type: 'success',
            canUndo: undoIdsRef.current.length > 0,
          });
          reload();
        }}
      />

      <UndoToast
        toast={toast}
        onUndo={async () => {
          const ids = undoIdsRef.current;
          undoIdsRef.current = null;
          setToast(null);
          if (!ids?.length) return;
          try {
            await api.undoStaleArchive(ids, { silent: true });
            setToast({ message: 'Archive undone.', type: 'success' });
          } catch {
            setToast({ message: 'Could not undo the archive.', type: 'error' });
          } finally {
            reload();
          }
        }}
        onDismiss={() => setToast(null)}
      />
    </div>
  );
}
